import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PageComponent } from '../../../../../services/website.service';

export interface CTAButton {
  text: string;
  url: string;
  style?: 'primary' | 'secondary' | 'outline';
}

export interface GalleryImage {
  url: string;
  alt?: string;
  caption?: string;
  link?: string;
  // Carousel specific fields
  title?: string;
  description?: string;
  ctaButtons?: CTAButton[];
}

@Component({
  selector: 'app-media-gallery-component',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './media-gallery-component.component.html',
  styleUrls: ['./media-gallery-component.component.scss']
})
export class MediaGalleryComponentComponent {
  @Input() component!: PageComponent;
  @Input() mode: 'preview' | 'edit' = 'preview';
  @Input() isSelected: boolean = false;
  @Output() styleChange = new EventEmitter<void>();

  lightboxOpen = false;
  lightboxIndex = 0;

  // Gallery Layout Options
  getLayoutType(): string {
    // 'grid' | 'masonry' | 'carousel' | 'justified' | 'slider'
    return this.component.data['layoutType'] || 'grid';
  }

  isFullWidth(): boolean {
    return this.component.data['fullWidth'] === true;
  }

  getFullWidthClass(): string {
    if (this.isFullWidth()) {
      return 'rounded-none';
    }
    return 'rounded-lg';
  }

  getColumns(): number {
    return this.component.data['columns'] || 3;
  }

  getGap(): number {
    return this.component.data['gap'] || 8;
  }

  getImages(): GalleryImage[] {
    return this.component.data['images'] || [];
  }

  getHeading(): string {
    return this.component.data['heading'] || '';
  }

  // Display Options
  getShowCaptions(): boolean {
    return this.component.data['showCaptions'] !== false;
  }

  getCaptionPosition(): string {
    // 'below' | 'overlay' | 'hover'
    return this.component.data['captionPosition'] || 'below';
  }

  getImageFit(): string {
    // 'cover' | 'contain' | 'fill'
    return this.component.data['imageFit'] || 'cover';
  }

  getImageHeight(): number {
    return this.component.data['imageHeight'] || 200;
  }

  getImageHeightUnit(): string {
    return this.component.data['imageHeightUnit'] || 'px';
  }

  getAspectRatio(): string {
    // 'auto' | '1:1' | '4:3' | '16:9' | '3:2' | '2:3'
    return this.component.data['aspectRatio'] || 'auto';
  }

  // Interaction Options
  getEnableLightbox(): boolean {
    return this.component.data['enableLightbox'] !== false;
  }

  getEnableZoom(): boolean {
    return this.component.data['enableZoom'] || false;
  }

  getHoverEffect(): string {
    // 'none' | 'zoom' | 'fade' | 'slide-up' | 'overlay'
    return this.component.data['hoverEffect'] || 'zoom';
  }

  // Carousel/Slider Options
  getAutoplay(): boolean {
    return this.component.data['autoplay'] || false;
  }

  getAutoplaySpeed(): number {
    return this.component.data['autoplaySpeed'] || 3000;
  }

  getShowNavigation(): boolean {
    return this.component.data['showNavigation'] !== false;
  }

  getShowDots(): boolean {
    return this.component.data['showDots'] !== false;
  }

  getInfiniteLoop(): boolean {
    return this.component.data['infiniteLoop'] !== false;
  }

  // Border and Styling
  getImageBorderRadius(): number {
    return this.component.data['imageBorderRadius'] || 8;
  }

  getShowImageBorder(): boolean {
    return this.component.data['showImageBorder'] || false;
  }

  getImageBorderColor(): string {
    return this.component.data['imageBorderColor'] || '#e5e7eb';
  }

  getImageBorderWidth(): number {
    return this.component.data['imageBorderWidth'] || 1;
  }

  getShadowStyle(): string {
    // 'none' | 'sm' | 'md' | 'lg' | 'xl'
    return this.component.data['shadowStyle'] || 'sm';
  }

  // Lazy Loading
  getLazyLoad(): boolean {
    return this.component.data['lazyLoad'] !== false;
  }

  // Styling getters (shared pattern)
  getBackgroundType(): string {
    return this.component.data['backgroundType'] || 'solid';
  }

  getBackgroundColor(): string {
    return this.component.data['backgroundColor'] || '#ffffff';
  }

  getGradientFrom(): string {
    return this.component.data['gradientFrom'] || '#ffffff';
  }

  getGradientTo(): string {
    return this.component.data['gradientTo'] || '#ffffff';
  }

  getBackgroundImage(): string {
    return this.component.data['backgroundImage'] || '';
  }

  getTitlePosition(): string {
    return this.component.data['titlePosition'] || 'left';
  }

  getHeadingColor(): string {
    return this.component.data['headingColor'] || '#111827';
  }

  getTextColor(): string {
    return this.component.data['textColor'] || '#374151';
  }

  getParallax(): boolean {
    return this.component.data['parallax'] || false;
  }

  getSectionHeight(): string {
    return this.component.data['sectionHeight'] || '';
  }

  getHeightUnit(): string {
    return this.component.data['heightUnit'] || 'px';
  }

  getPreviewStyles(): { [key: string]: string } {
    const styles: { [key: string]: string } = {};
    const backgroundType = this.getBackgroundType();

    if (backgroundType === 'solid') {
      styles['background-color'] = this.getBackgroundColor();
    } else if (backgroundType === 'gradient') {
      styles['background'] = `linear-gradient(to bottom, ${this.getGradientFrom()}, ${this.getGradientTo()})`;
    } else if (backgroundType === 'image') {
      const bgImage = this.getBackgroundImage();
      if (bgImage) {
        styles['background-image'] = `url(${bgImage})`;
        styles['background-size'] = 'cover';
        styles['background-position'] = 'center';
        if (this.getParallax()) {
          styles['background-attachment'] = 'fixed';
        }
      }
    }

    const sectionHeight = this.getSectionHeight();
    if (sectionHeight) {
      const unit = this.getHeightUnit();
      styles['min-height'] = `${sectionHeight}${unit}`;
    }

    return styles;
  }

  getTitlePositionClass(): string {
    const position = this.getTitlePosition();
    switch (position) {
      case 'center':
        return 'text-center';
      case 'right':
        return 'text-right';
      default:
        return 'text-left';
    }
  }

  getGridColumnsClass(): string {
    const cols = this.getColumns();
    const gridClasses: { [key: number]: string } = {
      1: 'grid-cols-1',
      2: 'grid-cols-1 sm:grid-cols-2',
      3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
      4: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4',
      5: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5',
      6: 'grid-cols-2 sm:grid-cols-4 lg:grid-cols-6',
    };
    return gridClasses[cols] || gridClasses[3];
  }

  getGapClass(): string {
    const gap = this.getGap();
    if (gap <= 4) return 'gap-1';
    if (gap <= 8) return 'gap-2';
    if (gap <= 12) return 'gap-3';
    if (gap <= 16) return 'gap-4';
    if (gap <= 24) return 'gap-6';
    return 'gap-8';
  }

  getShadowClass(): string {
    const shadow = this.getShadowStyle();
    const shadowClasses: { [key: string]: string } = {
      'none': '',
      'sm': 'shadow-sm',
      'md': 'shadow-md',
      'lg': 'shadow-lg',
      'xl': 'shadow-xl',
    };
    return shadowClasses[shadow] || '';
  }

  getHoverEffectClass(): string {
    const effect = this.getHoverEffect();
    switch (effect) {
      case 'zoom':
        return 'hover:scale-105';
      case 'fade':
        return 'hover:opacity-80';
      default:
        return '';
    }
  }

  getAspectRatioClass(): string {
    const ratio = this.getAspectRatio();
    switch (ratio) {
      case '1:1':
        return 'aspect-square';
      case '4:3':
        return 'aspect-[4/3]';
      case '16:9':
        return 'aspect-video';
      case '3:2':
        return 'aspect-[3/2]';
      case '2:3':
        return 'aspect-[2/3]';
      default:
        return '';
    }
  }

  getImageStyles(): { [key: string]: string } {
    const styles: { [key: string]: string } = {};

    styles['border-radius'] = `${this.getImageBorderRadius()}px`;

    if (this.getShowImageBorder()) {
      styles['border'] = `${this.getImageBorderWidth()}px solid ${this.getImageBorderColor()}`;
    }

    const ratio = this.getAspectRatio();
    if (ratio === 'auto') {
      const height = this.getImageHeight();
      const unit = this.getImageHeightUnit();
      styles['height'] = `${height}${unit}`;
    }

    styles['object-fit'] = this.getImageFit();

    return styles;
  }

  hasImages(): boolean {
    const images = this.getImages();
    return images && images.length > 0;
  }

  // Lightbox methods
  openLightbox(index: number): void {
    // Always block lightbox in page editor preview
    if (this.mode === 'preview' || this.mode === 'edit') return;
    if (this.getEnableLightbox()) {
      this.lightboxIndex = index;
      this.lightboxOpen = true;
      // Prevent body scroll while lightbox is open
      document.body.style.overflow = 'hidden';
    }
  }

  closeLightbox(): void {
    this.lightboxOpen = false;
    document.body.style.overflow = '';
  }

  nextImage(): void {
    const images = this.getImages();
    if (this.getInfiniteLoop()) {
      this.lightboxIndex = (this.lightboxIndex + 1) % images.length;
    } else if (this.lightboxIndex < images.length - 1) {
      this.lightboxIndex++;
    }
  }

  prevImage(): void {
    const images = this.getImages();
    if (this.getInfiniteLoop()) {
      this.lightboxIndex = (this.lightboxIndex - 1 + images.length) % images.length;
    } else if (this.lightboxIndex > 0) {
      this.lightboxIndex--;
    }
  }

  getCurrentLightboxImage(): GalleryImage | null {
    const images = this.getImages();
    if (images.length > 0 && this.lightboxIndex < images.length) {
      return images[this.lightboxIndex];
    }
    return null;
  }

  // Carousel state
  carouselIndex = 0;

  nextSlide(): void {
    const images = this.getImages();
    if (this.getInfiniteLoop()) {
      this.carouselIndex = (this.carouselIndex + 1) % images.length;
    } else if (this.carouselIndex < images.length - 1) {
      this.carouselIndex++;
    }
  }

  prevSlide(): void {
    const images = this.getImages();
    if (this.getInfiniteLoop()) {
      this.carouselIndex = (this.carouselIndex - 1 + images.length) % images.length;
    } else if (this.carouselIndex > 0) {
      this.carouselIndex--;
    }
  }

  goToSlide(index: number): void {
    this.carouselIndex = index;
  }

  getCurrentCarouselImage(): GalleryImage | null {
    const images = this.getImages();
    if (images.length > 0 && this.carouselIndex < images.length) {
      return images[this.carouselIndex];
    }
    return null;
  }

  getCarouselTitle(): string {
    const currentImage = this.getCurrentCarouselImage();
    return currentImage?.title || '';
  }

  getCarouselDescription(): string {
    const currentImage = this.getCurrentCarouselImage();
    return currentImage?.description || '';
  }

  getCarouselCTAs(): CTAButton[] {
    const currentImage = this.getCurrentCarouselImage();
    return currentImage?.ctaButtons || [];
  }

  // Logo Ticker getters
  getTickerSpeed(): number {
    return this.component.data['tickerSpeed'] || 30;
  }

  getTickerImageWidth(): number {
    return this.component.data['tickerImageWidth'] || 160;
  }

  getTickerPauseOnHover(): boolean {
    return this.component.data['tickerPauseOnHover'] !== false;
  }

  getTickerPadding(): number {
    return this.component.data['tickerPadding'] ?? 10;
  }

  getTickerMode(): string {
    return this.component.data['tickerMode'] || 'continuous';
  }

  getTickerDuration(): number {
    const images = this.getImages();
    if (images.length === 0) return 10;
    const oneSetWidth = images.length * (this.getTickerImageWidth() + this.getGap());
    return oneSetWidth / this.getTickerSpeed();
  }

  getCTAClass(style: 'primary' | 'secondary' | 'outline'): string {
    const baseClasses = 'inline-flex items-center px-4 py-2 rounded-md font-medium transition-colors';
    switch (style) {
      case 'primary':
        return `${baseClasses} bg-indigo-600 text-white hover:bg-indigo-700`;
      case 'secondary':
        return `${baseClasses} bg-gray-600 text-white hover:bg-gray-700`;
      case 'outline':
        return `${baseClasses} border-2 border-white text-white hover:bg-white hover:text-black`;
      default:
        return `${baseClasses} bg-indigo-600 text-white hover:bg-indigo-700`;
    }
  }
}
